import {useCallback} from 'react';

import {dispatcher} from '@netx/core-next/instance/dispatcher';

import BatchJobModel, {type BatchJobId} from '@netx/core-app/models/batchJob';
import {useRpc} from '@netx/core-components/hooks/useRpc';
import {useSnackbar} from '@netx/core-components/Snackbar';

import {useProgressStore} from '../../';
import {errors, messages} from '../../../properties';
import {SetAssetAttributesSchema} from '../../../schema';

export const poll = (
	uuid: BatchJobId,
	updatedData: SetAssetAttributesSchema[],
	progress: ReturnType<typeof useProgressStore>,
) => {
	const batchJob = new BatchJobModel({uuid}, {pollInterval: 500});

	return new Promise<void>((resolve, reject) => {
		batchJob
			.on('progress', (model, prog) => progress.update(prog))
			.on('complete', () => {
				batchJob.garbageCollect();
				dispatcher.trigger('asset:tagPanel:grid:submit:succeeded', updatedData);
				resolve();
			})
			.on('error', reject);
	}).finally(() => {
		batchJob.garbageCollect();
	});
};

export default function useSet() {
	const rpc = useRpc();
	const snackbar = useSnackbar();

	const save = useCallback(
		async (
			updatedData: SetAssetAttributesSchema[],
			progress: ReturnType<typeof useProgressStore>,
		) => {
			try {
				const uuid = await rpc<string>('setAssetAttributes', [updatedData]);
				await poll(uuid, updatedData, progress);
				snackbar({
					message: messages.saveSuccess,
					severity: 'success',
				});
			} catch (error: any) {
				snackbar({
					message:
						errors.update[error?.code as keyof typeof errors['update']] ||
						errors.update.default,
					severity: 'error',
				});
				return false;
			}

			return true;
		},
		[],
	);

	return save;
}
